import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'localization';
import './PublisherDetailOrganism.scss';
import { usePublisherDetail } from '../../../hooks/usePublisherDetail';
import { useFollowPublisher } from '../../../hooks/useFollowPublisher';
import { useMarketplacePermissions } from '../../../hooks/useMarketplacePermissions';
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import MessageAtom from '../../atoms/Message/MessageAtom';
import FollowButtonAtom from '../../atoms/marketplace/FollowButtonAtom';
import ScopeCoinAtom from '../../atoms/marketplace/ScopeCoinAtom';
import SocialLinksMolecule from '../../molecules/marketplace/SocialLinksMolecule';
import PublisherStatusCalloutMolecule from '../../molecules/marketplace/PublisherStatusCalloutMolecule';
import ManagerReviewBarMolecule from '../../molecules/marketplace/ManagerReviewBarMolecule';
import ChannelCardMolecule from '../../molecules/marketplace/ChannelCardMolecule';

export interface PublisherDetailOrganismProps {
  slug: string;
  onBack?: () => void;
  onChannelClick?: (channelId: string) => void;
  onManage?: (slug: string) => void;
  onCreateChannel?: () => void;
  onShare?: () => void;
  onSubmit?: (slug: string) => void;
  onApprove?: (slug: string) => void;
  onReject?: (slug: string, note: string) => void;
  className?: string;
}

const STATUS_PILL_KEY: Record<string, string> = {
  draft: 'marketplace.publishers.statusPill.draft',
  pending_review: 'marketplace.publishers.statusPill.pendingReview',
  published: 'marketplace.publishers.statusPill.published',
  rejected: 'marketplace.publishers.statusPill.rejected',
};

const STATUS_PILL_ICON: Record<string, string> = {
  draft: 'pi-file-edit',
  pending_review: 'pi-clock',
  published: 'pi-check-circle',
  rejected: 'pi-times-circle',
};

export const PublisherDetailOrganism: React.FC<PublisherDetailOrganismProps> = ({
  slug,
  onBack,
  onChannelClick,
  onManage,
  onCreateChannel,
  onShare,
  onSubmit,
  onApprove,
  onReject,
  className = '',
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { publisher, channels, loading, error, refetch } = usePublisherDetail(slug);
  const { toggling, toggle } = useFollowPublisher();
  const {
    permissions,
    unavailable: permissionsUnavailable,
    refetch: refetchPermissions,
  } = useMarketplacePermissions(slug);

  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);

  const handleFollowClick = useCallback(async () => {
    if (!publisher) return;
    const result = await toggle(publisher.slug, publisher.isFollowed);
    if (result) refetch();
  }, [publisher, toggle, refetch]);

  const handleApprove = useCallback(async () => {
    if (!publisher || !onApprove) return;
    setApproveLoading(true);
    try {
      await onApprove(publisher.slug);
      refetch();
    } finally {
      setApproveLoading(false);
    }
  }, [publisher, onApprove, refetch]);

  const handleReject = useCallback(async (note: string) => {
    if (!publisher || !onReject) return;
    setRejectLoading(true);
    try {
      await onReject(publisher.slug, note);
      refetch();
    } finally {
      setRejectLoading(false);
    }
  }, [publisher, onReject, refetch]);

  const handleManage = useCallback(() => {
    if (!publisher) return;
    if (onManage) {
      onManage(publisher.slug);
    } else {
      navigate(`/marketplace/publishers/${publisher.slug}/manage`);
    }
  }, [publisher, onManage, navigate]);

  const handleShare = useCallback(() => {
    onShare?.();
  }, [onShare]);

  const handleChannelClick = useCallback((channelId: string) => {
    if (onChannelClick) {
      onChannelClick(channelId);
    } else if (publisher) {
      // Navigate using the pretty route so back button returns to publisher, not marketplace
      navigate(`/marketplace/${encodeURIComponent(publisher.slug)}/${encodeURIComponent(channelId)}`);
    }
  }, [onChannelClick, publisher, navigate]);

  const classes = ['marketplace-publisher-detail', className].filter(Boolean).join(' ');

  if (loading && !publisher) {
    return (
      <div className={classes}>
        {onBack && (
          <div className="marketplace-publisher-detail__breadcrumb">
            <button type="button" className="marketplace-publisher-detail__back" onClick={onBack}>
              <i className="pi pi-arrow-left" aria-hidden="true" />
              <span>{t('marketplace.publishers.detail.back')}</span>
            </button>
          </div>
        )}
        <div className="marketplace-publisher-detail__loading">
          <span className="marketplace-marketplace__spinner" />
          <p>{t('marketplace.common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes}>
        {onBack && (
          <div className="marketplace-publisher-detail__breadcrumb">
            <button type="button" className="marketplace-publisher-detail__back" onClick={onBack}>
              <i className="pi pi-arrow-left" aria-hidden="true" />
              <span>{t('marketplace.publishers.detail.back')}</span>
            </button>
          </div>
        )}
        <div className="marketplace-publisher-detail__error">
          <p className="marketplace-marketplace__error-message">{error}</p>
          <button type="button" className="marketplace-marketplace__retry-btn" onClick={refetch}>
            {t('marketplace.common.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!publisher) return null;

  const totalSubs = channels.reduce((s, ch) => s + (ch.subscriber_count ?? 0), 0);
  const totalItems = channels.reduce((s, ch) => s + (ch.item_count ?? 0), 0);

  // Permission-derived display flags (fail-closed: null permissions = visitor; an unavailable
  // answer is every flag false, so the controls hide and the notice below says why)
  const canManage = permissions?.canEdit ?? false;
  const isMarketplaceManager = permissions?.canApprove ?? false;
  const showManageControls = canManage || isMarketplaceManager;

  const bannerStyle = {
    background: [
      `radial-gradient(ellipse at 80% 20%, hsla(${publisher.hue}, 75%, 55%, 0.4) 0%, transparent 55%)`,
      `radial-gradient(ellipse at 15% 90%, hsla(${(publisher.hue + 50) % 360}, 65%, 45%, 0.35) 0%, transparent 50%)`,
      `linear-gradient(135deg, hsl(${publisher.hue} 55% 18%) 0%, hsl(${publisher.hue} 60% 12%) 50%, #062C58 100%)`,
    ].join(', '),
  } as React.CSSProperties;

  const statusPillKey = STATUS_PILL_KEY[publisher.status];

  return (
    <div className={classes}>
      {/* Single breadcrumb line with back button and clickable links */}
      <nav
        className="marketplace-publisher-detail__breadcrumb"
        aria-label={publisher.display_name}
      >
        {onBack && (
          <button
            type="button"
            className="marketplace-publisher-detail__back"
            aria-label={t('marketplace.publishers.detail.back')}
            onClick={onBack}
          >
            <i className="pi pi-arrow-left" aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          className="marketplace-publisher-detail__crumb-link"
          onClick={onBack}
        >
          {t('marketplace.publishers.detail.breadcrumb.marketplace')}
        </button>
        <span className="marketplace-publisher-detail__crumb-sep">{'›'}</span>
        <button
          type="button"
          className="marketplace-publisher-detail__crumb-link"
          onClick={onBack}
        >
          {t('marketplace.publishers.detail.breadcrumb.publishers')}
        </button>
        <span className="marketplace-publisher-detail__crumb-sep">{'›'}</span>
        <strong className="marketplace-publisher-detail__crumb-current">
          {publisher.display_name}
        </strong>
      </nav>

      {permissionsUnavailable && (
        <div
          className="marketplace-publisher-detail__alert-row marketplace-publisher-detail__permissions-unavailable"
          data-testid="publisher-permissions-unavailable"
        >
          <MessageAtom severity="warn" text={t('marketplace.publishers.detail.permissionsUnavailable')} />
          <ButtonAtom
            type="button"
            variant="text"
            size="small"
            label={t('common.retry')}
            onClick={refetchPermissions}
          />
        </div>
      )}

      {/* Owner status callout — only for non-published states */}
      {canManage && publisher.status !== 'published' && (
        <div className="marketplace-publisher-detail__alert-row">
          <PublisherStatusCalloutMolecule
            status={publisher.status}
            reviewerNote={publisher.reviewer_note}
          />
          {onSubmit && (publisher.status === 'draft' || publisher.status === 'rejected') && (
            <button
              type="button"
              className="marketplace-publisher-detail__submit-btn"
              onClick={() => onSubmit(publisher.slug)}
            >
              {t('marketplace.publishers.detail.submitButton')}
            </button>
          )}
        </div>
      )}

      {/* Marketplace manager review bar */}
      <ManagerReviewBarMolecule
        permissions={permissions}
        publisherStatus={publisher.status}
        onApprove={onApprove ? handleApprove : undefined}
        onReject={onReject ? handleReject : undefined}
        approveLoading={approveLoading}
        rejectLoading={rejectLoading}
        className="marketplace-publisher-detail__alert-row"
      />

      {/* Full-bleed banner: 220px with hue gradient + decorative grid + floating rings */}
      <div className="marketplace-publisher-detail__banner" style={bannerStyle} aria-hidden="true">
        <svg
          className="marketplace-publisher-detail__banner-grid"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern
              id={`grid-${publisher.id}`}
              width="60"
              height="60"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 60 0 L 0 0 0 60"
                fill="none"
                stroke="rgba(255,255,255,0.4)"
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill={`url(#grid-${publisher.id})`} />
        </svg>
        <div className="marketplace-publisher-detail__ring marketplace-publisher-detail__ring--large" />
        <div className="marketplace-publisher-detail__ring marketplace-publisher-detail__ring--small" />
      </div>

      {/* Profile chrome card — overlaps banner by 72px */}
      <div className="marketplace-publisher-detail__chrome-wrap">
        <div className="marketplace-publisher-detail__chrome">
          {/* Glyph — pulls 36px above the card top to overlap the banner */}
          <div className="marketplace-publisher-detail__glyph-wrap">
            <ScopeCoinAtom
              scope="center"
              glyph={publisher.glyph}
              channelName={publisher.display_name}
              glyphKind={publisher.glyph_kind}
              hue={publisher.hue}
              size="xl"
              isOfficial={publisher.is_official}
            />
          </div>

          {/* Identity column */}
          <div className="marketplace-publisher-detail__identity">
            <div className="marketplace-publisher-detail__name-row">
              <h1 className="marketplace-publisher-detail__name">{publisher.display_name}</h1>
              {publisher.is_official && (
                <StatusBadgeAtom
                  tone="success"
                  icon="pi pi-verified"
                  label={t('marketplace.publishers.officialBadge')}
                  size="medium"
                  className="marketplace-publisher-detail__official"
                />
              )}
              {statusPillKey && (
                <span
                  className={`marketplace-publisher-detail__status-pill marketplace-publisher-detail__status-pill--${publisher.status}`}
                >
                  <i className={`pi ${STATUS_PILL_ICON[publisher.status]}`} aria-hidden="true" />
                  {t(statusPillKey)}
                </span>
              )}
            </div>

            <div className="marketplace-publisher-detail__meta-row">
              <span className="marketplace-publisher-detail__slug">/{publisher.slug}</span>
              <SocialLinksMolecule
                website={publisher.website ?? undefined}
                socials={publisher.socials ?? undefined}
                className="marketplace-publisher-detail__socials"
              />
            </div>

            {publisher.bio && (
              <p className="marketplace-publisher-detail__bio">{publisher.bio}</p>
            )}

            {/* Inline stats strip */}
            <div className="marketplace-publisher-detail__stats-strip">
              <span className="marketplace-publisher-detail__inline-stat">
                <i
                  className="pi pi-box marketplace-publisher-detail__inline-stat-icon"
                  style={{ color: `hsl(${publisher.hue} 55% 40%)` }}
                  aria-hidden="true"
                />
                <span className="marketplace-publisher-detail__inline-stat-value">
                  {channels.length}
                </span>
                <span className="marketplace-publisher-detail__inline-stat-label">
                  {t('marketplace.publishers.detail.statChannels')}
                </span>
              </span>
              <span
                className="marketplace-publisher-detail__inline-stat"
                title={t('marketplace.publishers.detail.statChannelSubsTooltip')}
              >
                <i
                  className="pi pi-users marketplace-publisher-detail__inline-stat-icon"
                  style={{ color: `hsl(${publisher.hue} 55% 40%)` }}
                  aria-hidden="true"
                />
                <span className="marketplace-publisher-detail__inline-stat-value">
                  {totalSubs.toLocaleString()}
                </span>
                <span className="marketplace-publisher-detail__inline-stat-label">
                  {t('marketplace.publishers.detail.statChannelSubs')}
                </span>
              </span>
              <span className="marketplace-publisher-detail__inline-stat">
                <i
                  className="pi pi-th-large marketplace-publisher-detail__inline-stat-icon"
                  style={{ color: `hsl(${publisher.hue} 55% 40%)` }}
                  aria-hidden="true"
                />
                <span className="marketplace-publisher-detail__inline-stat-value">
                  {totalItems}
                </span>
                <span className="marketplace-publisher-detail__inline-stat-label">
                  {t('marketplace.publishers.detail.statItems')}
                </span>
              </span>
            </div>
          </div>

          {/* Action stack */}
          <div className="marketplace-publisher-detail__actions">
            {showManageControls ? (
              <>
                <button
                  type="button"
                  className="marketplace-publisher-detail__btn marketplace-publisher-detail__btn--primary"
                  onClick={handleManage}
                >
                  <i className="pi pi-cog" aria-hidden="true" />
                  {t('marketplace.publishers.detail.manage')}
                </button>
                {canManage && publisher.status === 'published' && onCreateChannel && (
                  <button
                    type="button"
                    className="marketplace-publisher-detail__btn marketplace-publisher-detail__btn--secondary"
                    onClick={onCreateChannel}
                  >
                    <i className="pi pi-plus" aria-hidden="true" />
                    {t('marketplace.publishers.detail.addChannel')}
                  </button>
                )}
                <button
                  type="button"
                  className="marketplace-publisher-detail__btn marketplace-publisher-detail__btn--ghost"
                  onClick={handleShare}
                >
                  <i className="pi pi-share-alt" aria-hidden="true" />
                  {t('marketplace.publishers.detail.share')}
                </button>
              </>
            ) : (
              <>
                <FollowButtonAtom
                  isFollowed={publisher.isFollowed}
                  followerCount={publisher.followerCount}
                  loading={toggling}
                  onClick={handleFollowClick}
                  followLabel={t('marketplace.publishers.detail.followPublisher')}
                  followingLabel={t('marketplace.publishers.following')}
                />
                <button
                  type="button"
                  className="marketplace-publisher-detail__btn marketplace-publisher-detail__btn--ghost"
                  onClick={handleShare}
                >
                  <i className="pi pi-share-alt" aria-hidden="true" />
                  {t('marketplace.publishers.detail.share')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Channels area */}
      <div className="marketplace-publisher-detail__channels-area">
        <div className="marketplace-publisher-detail__channels-header">
          <h2 className="marketplace-publisher-detail__channels-title">
            {t('marketplace.publishers.detail.channelsBy', { name: publisher.display_name })}
          </h2>
          <span className="marketplace-publisher-detail__channels-count">
            {t('marketplace.publishers.detail.channelCount', { count: channels.length })}
          </span>
        </div>

        {channels.length > 0 ? (
          <div className="marketplace-publisher-detail__channels-grid">
            {channels.map((channel) => (
              <ChannelCardMolecule
                key={channel.id}
                channel={channel}
                itemCount={channel.item_count ?? 0}
                isSubscribed={false}
                isMandatory={false}
                isOwner={false}
                ownerLabel={publisher.display_name}
                onClick={handleChannelClick}
                onSubscribeToggle={() => undefined}
              />
            ))}
          </div>
        ) : (
          <div className="marketplace-publisher-detail__channels-empty">
            <i className="pi pi-box marketplace-publisher-detail__channels-empty-icon" aria-hidden="true" />
            <p className="marketplace-publisher-detail__channels-empty-title">
              {t('marketplace.publishers.detail.noChannelsYet')}
            </p>
            <p className="marketplace-publisher-detail__channels-empty-hint">
              {canManage
                ? t('marketplace.publishers.detail.noChannelsOwnerHint')
                : t('marketplace.publishers.detail.noChannelsVisitorHint')}
            </p>
            {canManage && publisher.status === 'published' && onCreateChannel && (
              <button
                type="button"
                className="marketplace-publisher-detail__btn marketplace-publisher-detail__btn--primary"
                onClick={onCreateChannel}
              >
                <i className="pi pi-plus" aria-hidden="true" />
                {t('marketplace.publishers.detail.createFirstChannel')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublisherDetailOrganism;
